/* eslint-disable react-hooks/rules-of-hooks */
import React from 'react'
import { FORM_ERROR } from 'final-form'

import SEO from '../../../../../apps/www/src/components/seo'
import WbacWrapper from '../../components/wrapper/wrapper.component'
import Steps from '../../components/steps'
import ValuationPanel from '@cinch/oxd-components/valuation-panel/src'
import AssumptionsPanel from '../../components/assumptions-panel/assumptions-panel.component'
import ValuationActions from '../../components/valuation-actions/valuation-actions.component'
import ImportantInformationPanel from '../../components/information-panel/information-panel.component'
import BookingPanel from '../../components/booking-panel/booking-panel.component'
import Panel from '@cinch/oxd-components/panel/src'
import Accordion from '@cinch/oxd-components/filter-accordion/src'
import RichText from '@cinch/oxd-components/rich-text/src'
import PaymentInfoPanel from '@cinch/oxd-components/payment-info-panel/src'

import * as Storage from '@cinch/oxd-utils/storage/src'
import { WbacService } from '@cinch/oxd-utils/requests/src'

import { ValuationResponse, VehicleInformation, Pages } from '../../types'

import * as styles from './valuation.module.css'
import { currencyValueFormatter } from '@cinch/oxd-utils/form/src'

interface Props {
  location?: any
  navigate?: (to: string, state?: any) => any
  transaction: React.ReactNode
  paymentTerms: React.ReactNode
  guarantee: React.ReactNode
  path: Pages
  i18n: Cinch.WebAppTranslations['sellVehicle']['valuation']
}

const scrollToRef = (ref: React.RefObject<null | HTMLDivElement>) => {
  if (typeof window !== 'undefined') {
    if (ref && ref.current) {
      if ('scrollBehavior' in document.documentElement.style) {
        ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
      } else {
        window.scrollTo(0, ref.current.offsetTop)
      }
    }
  }
}

const ValuationComponent: React.FC<Props> = ({
  location,
  navigate = () => null,
  transaction,
  paymentTerms,
  guarantee,
  path,
  i18n,
}) => {
  const vehicleInformation: VehicleInformation = location.state.vehicleInformation
  const valuation: ValuationResponse = location.state.valuation
  const paymentPanels = [i18n.paymentPanels.hurry, i18n.paymentPanels.nextDay, i18n.paymentPanels.premierPayment]

  if (!location.state || !vehicleInformation || !valuation) {
    setTimeout(() => navigate('/sell'))

    return null
  }

  // To explicitly handle the expanded state of the transaction fee accordion
  const [isAccordionExpanded, updateAccordionExpanded] = React.useState(false)
  const toggleAccordion = (_accordionId: any, expandedState: any) => updateAccordionExpanded(expandedState)

  const importantInfoRef = React.useRef<HTMLDivElement>(null)
  const bookingPanelRef = React.useRef<HTMLDivElement>(null)
  const handleOnClickSell = () => scrollToRef(bookingPanelRef)

  const transactionFeeRef = React.useRef<HTMLDivElement>(null)
  const handleOnClickFee = () => {
    updateAccordionExpanded(true)
    scrollToRef(transactionFeeRef)
  }

  const onChanges = () => {
    navigate('/sell/valuation/update', { state: { vehicleInformation, valuation } })
  }

  const onSubmit = async (date: string, slotId: number) => {
    const { token: storageToken } = Storage.get(Storage.StorageKeys.wbac)

    try {
      const { data: personalInformation } = await WbacService.get(
        `/valuations/valuation/${valuation.valuationGuid}/PersonalInformation`,
        {
          headers: { Authorization: `JWT ${storageToken}` },
        },
      )

      navigate('/sell/appointment/verify', {
        state: {
          vehicleInformation,
          personalInformation,
          appointmentParams: {
            date,
            slotId,
          },
        },
      })
    } catch (e) {
      return { [FORM_ERROR]: e.message }
    }
  }

  return (
    <>
      <SEO noIndex={true} />
      <WbacWrapper>
        <Steps page={path} />

        <section className={styles.main}>
          <div className={styles.valuation}>
            <ValuationPanel
              value={currencyValueFormatter(valuation.vehiclePriceOffered)}
              i18n={{
                title: i18n.panelTitle,
                transactionFee: i18n.panelBody,
                ctaText: i18n.ctaText,
              }}
              onChanges={onChanges}
            />
            <AssumptionsPanel vehicleInformation={vehicleInformation} valuation={valuation} />
            <ValuationActions onClickSell={handleOnClickSell} i18n={i18n.valuationActions} />
            <div ref={importantInfoRef}>
              <ImportantInformationPanel onClickFee={handleOnClickFee} />
            </div>
          </div>

          <div className={styles.panels}>
            <BookingPanel
              onSubmit={onSubmit}
              vehiclePriceOffered={valuation.vehiclePriceOffered}
              customerPostcode={valuation.customerPostcode}
            />

            <Panel title={i18n.paymentInfo}>
              <div ref={transactionFeeRef}>
                <Accordion title={i18n.transactionFee} id={1} toggle={toggleAccordion} isExpanded={isAccordionExpanded}>
                  <div className={styles.accordianContent}>
                    <RichText>{transaction}</RichText>
                  </div>
                </Accordion>
              </div>

              <Accordion title={i18n.getPaid}>
                <div className={styles.paymentPanels}>
                  {paymentPanels.map((props, index) => (
                    <PaymentInfoPanel key={index} {...props} />
                  ))}
                </div>
              </Accordion>

              <Accordion title={i18n.pricePromise}>
                <div className={styles.accordianContent}>
                  <RichText>{guarantee}</RichText>
                </div>
              </Accordion>

              <Accordion title={i18n.paymentTermsTitle}>
                <div className={styles.accordianContent}>
                  <RichText>{paymentTerms}</RichText>
                </div>
              </Accordion>
            </Panel>
          </div>
        </section>
      </WbacWrapper>
    </>
  )
}

export default ValuationComponent
